import type { ReactElement } from 'react';
import { useCallback, useEffect, useState } from 'react';
import type {
  FavoriteModelSummary,
  FollowedModelSummary,
  ModelNotificationPreferenceSummary,
  NotificationEvent,
  NotificationPreferences,
  ProfileVisibility,
  UserSpendLimits,
} from '@fetmode/types';
import { MAX_MODEL_FAVORITES, NOTIFICATION_EVENTS } from '@fetmode/types';
import { ApiError } from '@fetmode/api-client';
import { t, LanguageSwitcher } from '@fetmode/i18n';
import { formatUsername } from '@fetmode/utils';
import { Button } from '@fetmode/ui';
import { Link, useSearchParams } from 'react-router-dom';

import { useAuth } from '../auth/index.js';
import { TurnOnsPicker } from '../experience/TurnOnsPicker.js';
import {
  EMPTY_TURN_ONS_SELECTION,
  type TurnOnsSelection,
} from '../experience/turn-ons-catalog.js';
import { useLocaleAccountSync } from '../../shared/hooks/useLocaleAccountSync.js';
import './SettingsPage.css';

type SettingsTab =
  | 'profile'
  | 'turn-ons'
  | 'following'
  | 'favorites'
  | 'notifications'
  | 'spend'
  | 'language';

const SETTINGS_TABS = [
  'profile',
  'turn-ons',
  'favorites',
  'following',
  'notifications',
  'spend',
  'language',
] as const satisfies ReadonlyArray<SettingsTab>;

function parseOptionalCoinsInput(value: string): number | null {
  const trimmed = value.trim();
  if (trimmed.length === 0) {
    return null;
  }
  const parsed = Number(trimmed);
  if (!Number.isFinite(parsed) || parsed < 0) {
    return null;
  }
  return Math.floor(parsed);
}

function parseRequiredCoinsInput(value: string): number {
  const parsed = Number(value.trim());
  if (!Number.isFinite(parsed) || parsed < 0) {
    return 0;
  }
  return Math.floor(parsed);
}

function parseSettingsTab(value: string | null): SettingsTab {
  if ((SETTINGS_TABS as ReadonlyArray<string>).includes(value ?? '')) {
    return value as SettingsTab;
  }
  return 'profile';
}

interface ProfileForm {
  displayName: string;
  pronouns: string;
  location: string;
  bio: string;
  avatarUrl: string;
  profileVisibility: ProfileVisibility;
}

const EVENT_LABEL_KEYS: Record<NotificationEvent, string> = {
  modelLive: 'common.settings.eventModelLive',
  newContent: 'common.settings.eventNewContent',
  privateMessage: 'common.settings.eventPrivateMessage',
  tipsAndPurchases: 'common.settings.eventTipsAndPurchases',
};

const MODEL_EVENT_KEYS = ['modelLive', 'newContent', 'privateMessage'] as const;

export function SettingsPage(): ReactElement {
  const { api, user, setUser } = useAuth();
  const { persistLocale } = useLocaleAccountSync();
  const [searchParams, setSearchParams] = useSearchParams();
  const [profileForm, setProfileForm] = useState<ProfileForm | null>(null);
  const [isProfileLoading, setIsProfileLoading] = useState(false);
  const [turnOns, setTurnOns] = useState<TurnOnsSelection>(EMPTY_TURN_ONS_SELECTION);
  const [isTurnOnsLoading, setIsTurnOnsLoading] = useState(false);
  const [tab, setTab] = useState<SettingsTab>(() => parseSettingsTab(searchParams.get('tab')));
  const [following, setFollowing] = useState<FollowedModelSummary[]>([]);
  const [favorites, setFavorites] = useState<FavoriteModelSummary[]>([]);
  const [prefs, setPrefs] = useState<NotificationPreferences | null>(null);
  const [modelPrefs, setModelPrefs] = useState<ModelNotificationPreferenceSummary[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [spendLimits, setSpendLimits] = useState<UserSpendLimits | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSpendLoading, setIsSpendLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [savingModelId, setSavingModelId] = useState<string | null>(null);

  useEffect(() => {
    setTab(parseSettingsTab(searchParams.get('tab')));
  }, [searchParams]);

  const selectTab = (next: SettingsTab): void => {
    setTab(next);
    setSuccess(null);
    setSearchParams(
      (current) => {
        const params = new URLSearchParams(current);
        params.set('tab', next);
        return params;
      },
      { replace: true },
    );
  };

  const load = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const [followingResult, favoritesResult, prefsOutcome] = await Promise.all([
        api.listFollowing(),
        api.listFavorites(),
        api.getNotificationPreferences().then(
          (result) => ({ ok: true as const, result }),
          (err: unknown) => ({ ok: false as const, err }),
        ),
      ]);
      setFollowing(followingResult.following);
      setFavorites(favoritesResult.favorites);
      if (prefsOutcome.ok) {
        setPrefs(prefsOutcome.result.preferences);
        setModelPrefs(prefsOutcome.result.models);
      } else {
        setPrefs(null);
        setModelPrefs([]);
        setError(
          prefsOutcome.err instanceof ApiError
            ? prefsOutcome.err.message
            : t('common.settings.notificationsUnavailable'),
        );
      }
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.settings.loadError'));
    } finally {
      setIsLoading(false);
    }
  }, [api]);

  useEffect(() => {
    void load();
  }, [load]);

  const loadSpendLimits = useCallback(async () => {
    setIsSpendLoading(true);
    setError(null);
    try {
      const result = await api.getSpendLimits();
      setSpendLimits(result.limits);
    } catch (err) {
      setSpendLimits(null);
      setError(err instanceof ApiError ? err.message : t('common.settings.spendLoadError'));
    } finally {
      setIsSpendLoading(false);
    }
  }, [api]);

  useEffect(() => {
    if (tab === 'spend') {
      void loadSpendLimits();
    }
  }, [tab, loadSpendLimits]);

  const loadProfile = useCallback(async () => {
    if (!user?.username) {
      return;
    }
    setIsProfileLoading(true);
    setError(null);
    try {
      const { profile } = await api.getUserProfile(user.username);
      setProfileForm({
        displayName: profile.displayName,
        pronouns: profile.pronouns ?? '',
        location: profile.location ?? '',
        bio: profile.bio ?? '',
        avatarUrl: profile.avatarUrl ?? '',
        profileVisibility: profile.visibility,
      });
    } catch (err) {
      setProfileForm(null);
      setError(err instanceof ApiError ? err.message : t('common.settings.loadError'));
    } finally {
      setIsProfileLoading(false);
    }
  }, [api, user?.username]);

  useEffect(() => {
    if (tab === 'profile') {
      void loadProfile();
    }
  }, [tab, loadProfile]);

  const loadTurnOns = useCallback(async () => {
    setIsTurnOnsLoading(true);
    setError(null);
    try {
      const result = await api.getTurnOns();
      setTurnOns({
        genders: result.turnOns.genders,
        ageGroups: result.turnOns.ageGroups,
        ethnicities: result.turnOns.ethnicities,
        bodyTypes: result.turnOns.bodyTypes,
      });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.settings.loadError'));
    } finally {
      setIsTurnOnsLoading(false);
    }
  }, [api]);

  useEffect(() => {
    if (tab === 'turn-ons') {
      void loadTurnOns();
    }
  }, [tab, loadTurnOns]);

  const saveTurnOns = async (): Promise<void> => {
    setIsSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const result = await api.updateTurnOns({
        ...turnOns,
        completed: true,
      });
      setTurnOns({
        genders: result.turnOns.genders,
        ageGroups: result.turnOns.ageGroups,
        ethnicities: result.turnOns.ethnicities,
        bodyTypes: result.turnOns.bodyTypes,
      });
      const updatedUser = await api.getMe();
      setUser(updatedUser);
      setSuccess(t('common.turnOns.saved'));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.settings.saveError'));
    } finally {
      setIsSaving(false);
    }
  };

  const saveProfile = async (): Promise<void> => {
    if (!profileForm) {
      return;
    }
    setIsSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const trim = (value: string): string => value.trim();
      const result = await api.updateMyProfile({
        displayName: trim(profileForm.displayName),
        pronouns: trim(profileForm.pronouns) || null,
        location: trim(profileForm.location) || null,
        bio: trim(profileForm.bio) || null,
        avatarUrl: trim(profileForm.avatarUrl) || null,
        profileVisibility: profileForm.profileVisibility,
      });
      setProfileForm({
        displayName: result.profile.displayName,
        pronouns: result.profile.pronouns ?? '',
        location: result.profile.location ?? '',
        bio: result.profile.bio ?? '',
        avatarUrl: result.profile.avatarUrl ?? '',
        profileVisibility: result.profile.visibility,
      });
      setSuccess(t('common.settings.profileSaved'));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.settings.saveError'));
    } finally {
      setIsSaving(false);
    }
  };

  const moveFavorite = (index: number, direction: -1 | 1): void => {
    setFavorites((current) => {
      const nextIndex = index + direction;
      if (nextIndex < 0 || nextIndex >= current.length) {
        return current;
      }
      const next = [...current];
      const [item] = next.splice(index, 1);
      if (!item) {
        return current;
      }
      next.splice(nextIndex, 0, item);
      return next.map((favorite, rankIndex) => ({
        ...favorite,
        rank: rankIndex + 1,
      }));
    });
    setSuccess(null);
  };

  const saveFavoriteOrder = async (): Promise<void> => {
    setIsSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const result = await api.reorderFavorites({
        modelIds: favorites.map((favorite) => favorite.modelId),
      });
      setFavorites(result.favorites);
      setSuccess(t('common.settings.favoritesSaved'));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.settings.saveError'));
    } finally {
      setIsSaving(false);
    }
  };

  const removeFavorite = async (username: string): Promise<void> => {
    setIsSaving(true);
    setError(null);
    try {
      const result = await api.unfavoriteModel(username);
      setFavorites(result.favorites);
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.settings.saveError'));
    } finally {
      setIsSaving(false);
    }
  };

  const unfollow = async (username: string): Promise<void> => {
    setIsSaving(true);
    setError(null);
    try {
      await api.unfollowModel(username);
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.settings.saveError'));
    } finally {
      setIsSaving(false);
    }
  };

  const saveSpendLimits = async (): Promise<void> => {
    if (!spendLimits) {
      return;
    }
    setIsSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const result = await api.updateSpendLimits({
        dailyLimitCoins: spendLimits.dailyLimitCoins,
        sessionBudgetCoins: spendLimits.sessionBudgetCoins,
        autoTopUpEnabled: spendLimits.autoTopUpEnabled,
        autoTopUpTriggerCoins: spendLimits.autoTopUpTriggerCoins,
        autoTopUpAmountCoins: spendLimits.autoTopUpAmountCoins,
      });
      setSpendLimits(result.limits);
      setSuccess(t('common.settings.spendSaved'));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.settings.saveError'));
    } finally {
      setIsSaving(false);
    }
  };

  const saveNotifications = async (): Promise<void> => {
    if (!prefs) {
      return;
    }
    setIsSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const result = await api.updateNotificationPreferences({
        browserEnabled: prefs.browserEnabled,
        emailEnabled: prefs.emailEnabled,
        smsEnabled: prefs.smsEnabled,
        phoneE164: prefs.phoneE164,
        events: prefs.events,
      });
      setPrefs(result.preferences);
      setModelPrefs(result.models);
      setSuccess(t('common.settings.notificationsSaved'));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.settings.saveError'));
    } finally {
      setIsSaving(false);
    }
  };

  const updateModelNotification = async (
    model: ModelNotificationPreferenceSummary,
    key: (typeof MODEL_EVENT_KEYS)[number],
    value: boolean,
  ): Promise<void> => {
    setSavingModelId(model.modelId);
    setError(null);
    setSuccess(null);
    const next = { ...model.notifications, [key]: value };
    setModelPrefs((current) =>
      current.map((entry) =>
        entry.modelId === model.modelId ? { ...entry, notifications: next } : entry,
      ),
    );
    try {
      const result = await api.updateModelNotifications(model.username, {
        [key]: value,
      });
      if (result.relationship.notifications) {
        setModelPrefs((current) =>
          current.map((entry) =>
            entry.modelId === model.modelId
              ? { ...entry, notifications: result.relationship.notifications! }
              : entry,
          ),
        );
      }
      setSuccess(t('common.settings.modelNotificationsSaved'));
    } catch (err) {
      setModelPrefs((current) =>
        current.map((entry) =>
          entry.modelId === model.modelId
            ? { ...entry, notifications: model.notifications }
            : entry,
        ),
      );
      setError(err instanceof ApiError ? err.message : t('common.settings.saveError'));
    } finally {
      setSavingModelId(null);
    }
  };

  return (
    <div className="settings-page">
      <header className="settings-page__header">
        <h1 className="settings-page__title">{t('common.settings.title')}</h1>
        <p className="settings-page__hint">{t('common.settings.hint')}</p>
      </header>

      <div className="settings-page__tabs" role="tablist" aria-label={t('common.settings.title')}>
        {(
          [
            ['profile', t('common.settings.tabProfile')],
            ['turn-ons', t('common.settings.tabTurnOns')],
            ['favorites', t('common.settings.tabFavorites')],
            ['following', t('common.settings.tabFollowing')],
            ['notifications', t('common.settings.tabNotifications')],
            ['spend', t('common.settings.tabSpend')],
            ['language', t('common.settings.tabLanguage')],
          ] as const
        ).map(([id, label]) => (
          <button
            key={id}
            type="button"
            role="tab"
            aria-selected={tab === id}
            className={
              tab === id
                ? 'settings-page__tab settings-page__tab--active'
                : 'settings-page__tab'
            }
            onClick={() => {
              selectTab(id);
            }}
          >
            {label}
          </button>
        ))}
      </div>

      {isLoading ? (
        <p className="settings-page__muted">{t('common.settings.loading')}</p>
      ) : (
        <div className="settings-page__panel" role="tabpanel">
          {error ? <p className="fm-error">{error}</p> : null}
          {success ? <p className="settings-page__success">{success}</p> : null}

          {tab === 'profile' ? (
            isProfileLoading ? (
              <p className="settings-page__muted">{t('common.settings.loading')}</p>
            ) : profileForm ? (
              <>
                <p className="settings-page__muted">{t('common.settings.profileHint')}</p>
                {user?.username ? (
                  <p className="settings-page__muted">
                    <Link to={`/@${user.username}`} className="settings-page__name">
                      {t('common.settings.viewProfile')}
                    </Link>
                  </p>
                ) : null}

                <fieldset className="settings-page__fieldset">
                  <legend>{t('common.settings.profileSection')}</legend>
                  <label className="settings-page__field">
                    {t('common.settings.profileDisplayName')}
                    <input
                      className="fm-input"
                      type="text"
                      maxLength={60}
                      value={profileForm.displayName}
                      onChange={(event) => {
                        setProfileForm((current) =>
                          current ? { ...current, displayName: event.target.value } : current,
                        );
                      }}
                    />
                  </label>
                  <label className="settings-page__field">
                    {t('common.settings.profilePronouns')}
                    <input
                      className="fm-input"
                      type="text"
                      maxLength={40}
                      placeholder={t('common.settings.profilePronounsPlaceholder')}
                      value={profileForm.pronouns}
                      onChange={(event) => {
                        setProfileForm((current) =>
                          current ? { ...current, pronouns: event.target.value } : current,
                        );
                      }}
                    />
                  </label>
                  <label className="settings-page__field">
                    {t('common.settings.profileLocation')}
                    <input
                      className="fm-input"
                      type="text"
                      maxLength={80}
                      value={profileForm.location}
                      onChange={(event) => {
                        setProfileForm((current) =>
                          current ? { ...current, location: event.target.value } : current,
                        );
                      }}
                    />
                  </label>
                  <label className="settings-page__field">
                    {t('common.settings.profileBio')}
                    <textarea
                      className="fm-input"
                      rows={4}
                      maxLength={500}
                      value={profileForm.bio}
                      onChange={(event) => {
                        setProfileForm((current) =>
                          current ? { ...current, bio: event.target.value } : current,
                        );
                      }}
                    />
                  </label>
                  <label className="settings-page__field">
                    {t('common.settings.profileAvatarUrl')}
                    <input
                      className="fm-input"
                      type="url"
                      placeholder="https://…"
                      value={profileForm.avatarUrl}
                      onChange={(event) => {
                        setProfileForm((current) =>
                          current ? { ...current, avatarUrl: event.target.value } : current,
                        );
                      }}
                    />
                  </label>
                </fieldset>

                <fieldset className="settings-page__fieldset">
                  <legend>{t('common.settings.profileVisibility')}</legend>
                  <label className="settings-page__field">
                    {t('common.settings.profileVisibilityLabel')}
                    <select
                      className="fm-input"
                      value={profileForm.profileVisibility}
                      onChange={(event) => {
                        setProfileForm((current) =>
                          current
                            ? {
                                ...current,
                                profileVisibility: event.target.value as ProfileVisibility,
                              }
                            : current,
                        );
                      }}
                    >
                      <option value="PUBLIC">{t('common.settings.visibilityPublic')}</option>
                      <option value="MEMBERS">{t('common.settings.visibilityMembers')}</option>
                      <option value="FOLLOWERS">
                        {t('common.settings.visibilityFollowers')}
                      </option>
                    </select>
                  </label>
                </fieldset>

                <div className="settings-page__footer">
                  <Button
                    label={
                      isSaving ? t('common.settings.saving') : t('common.settings.saveProfile')
                    }
                    disabled={isSaving}
                    onClick={() => {
                      void saveProfile();
                    }}
                  />
                </div>
              </>
            ) : (
              <p className="settings-page__muted">{t('common.settings.profileUnavailable')}</p>
            )
          ) : null}

          {tab === 'turn-ons' ? (
            isTurnOnsLoading ? (
              <p className="settings-page__muted">{t('common.settings.loading')}</p>
            ) : (
              <>
                <p className="settings-page__muted">{t('common.turnOns.settingsHint')}</p>
                <TurnOnsPicker
                  value={turnOns}
                  onChange={setTurnOns}
                  disabled={isSaving}
                />
                <div className="settings-page__footer">
                  <Button
                    label={isSaving ? t('common.settings.saving') : t('common.turnOns.save')}
                    disabled={isSaving}
                    onClick={() => {
                      void saveTurnOns();
                    }}
                  />
                </div>
              </>
            )
          ) : null}

          {tab === 'favorites' ? (
            <>
              <p className="settings-page__muted">
                {t('common.settings.favoritesHint').replace(
                  '{{max}}',
                  String(MAX_MODEL_FAVORITES),
                )}
              </p>
              {favorites.length === 0 ? (
                <p className="settings-page__muted">{t('common.settings.favoritesEmpty')}</p>
              ) : (
                <ol className="settings-page__list">
                  {favorites.map((favorite, index) => (
                    <li key={favorite.modelId} className="settings-page__row">
                      <span className="settings-page__rank">#{favorite.rank}</span>
                      <div className="settings-page__row-main">
                        <Link to={`/@${favorite.username}`} className="settings-page__name">
                          {favorite.displayName}
                        </Link>
                        <span className="settings-page__handle">
                          {formatUsername(favorite.username)}
                        </span>
                      </div>
                      <div className="settings-page__row-actions">
                        <button
                          type="button"
                          className="settings-page__icon-btn"
                          disabled={index === 0 || isSaving}
                          aria-label={t('common.settings.moveUp')}
                          onClick={() => {
                            moveFavorite(index, -1);
                          }}
                        >
                          ↑
                        </button>
                        <button
                          type="button"
                          className="settings-page__icon-btn"
                          disabled={index === favorites.length - 1 || isSaving}
                          aria-label={t('common.settings.moveDown')}
                          onClick={() => {
                            moveFavorite(index, 1);
                          }}
                        >
                          ↓
                        </button>
                        <button
                          type="button"
                          className="settings-page__text-btn"
                          disabled={isSaving}
                          onClick={() => {
                            void removeFavorite(favorite.username);
                          }}
                        >
                          {t('common.settings.removeFave')}
                        </button>
                      </div>
                    </li>
                  ))}
                </ol>
              )}
              {favorites.length > 0 ? (
                <div className="settings-page__footer">
                  <Button
                    label={
                      isSaving
                        ? t('common.settings.saving')
                        : t('common.settings.saveFavoriteOrder')
                    }
                    disabled={isSaving}
                    onClick={() => {
                      void saveFavoriteOrder();
                    }}
                  />
                </div>
              ) : null}
            </>
          ) : null}

          {tab === 'following' ? (
            <>
              <p className="settings-page__muted">{t('common.settings.followingHint')}</p>
              {following.length === 0 ? (
                <p className="settings-page__muted">{t('common.settings.followingEmpty')}</p>
              ) : (
                <ul className="settings-page__list">
                  {following.map((item) => (
                    <li key={item.modelId} className="settings-page__row">
                      <div className="settings-page__row-main">
                        <Link to={`/@${item.username}`} className="settings-page__name">
                          {item.displayName}
                        </Link>
                        <span className="settings-page__handle">
                          {formatUsername(item.username)}
                          {item.isFavorite
                            ? ` · ${t('common.settings.faveBadge').replace('{{rank}}', String(item.favoriteRank))}`
                            : ''}
                        </span>
                      </div>
                      <button
                        type="button"
                        className="settings-page__text-btn"
                        disabled={isSaving}
                        onClick={() => {
                          void unfollow(item.username);
                        }}
                      >
                        {t('common.settings.unfollow')}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </>
          ) : null}

          {tab === 'spend' ? (
            isSpendLoading ? (
              <p className="settings-page__muted">{t('common.settings.spendLoading')}</p>
            ) : spendLimits ? (
              <>
                <p className="settings-page__muted">{t('common.settings.spendHint')}</p>

                <fieldset className="settings-page__fieldset">
                  <legend>{t('common.settings.spendLimitsSection')}</legend>
                  <label className="settings-page__field">
                    {t('common.settings.spendDailyLimit')}
                    <input
                      className="fm-input"
                      type="number"
                      min={0}
                      step={1}
                      placeholder={t('common.settings.spendNoLimitPlaceholder')}
                      value={spendLimits.dailyLimitCoins ?? ''}
                      onChange={(event) => {
                        setSpendLimits((current) =>
                          current
                            ? {
                                ...current,
                                dailyLimitCoins: parseOptionalCoinsInput(event.target.value),
                              }
                            : current,
                        );
                      }}
                    />
                  </label>
                  <label className="settings-page__field">
                    {t('common.settings.spendSessionBudget')}
                    <input
                      className="fm-input"
                      type="number"
                      min={0}
                      step={1}
                      placeholder={t('common.settings.spendNoLimitPlaceholder')}
                      value={spendLimits.sessionBudgetCoins ?? ''}
                      onChange={(event) => {
                        setSpendLimits((current) =>
                          current
                            ? {
                                ...current,
                                sessionBudgetCoins: parseOptionalCoinsInput(event.target.value),
                              }
                            : current,
                        );
                      }}
                    />
                  </label>
                </fieldset>

                <fieldset className="settings-page__fieldset">
                  <legend>{t('common.settings.spendAutoTopUpSection')}</legend>
                  <label className="settings-page__check">
                    <input
                      type="checkbox"
                      checked={spendLimits.autoTopUpEnabled}
                      onChange={(event) => {
                        setSpendLimits((current) =>
                          current
                            ? { ...current, autoTopUpEnabled: event.target.checked }
                            : current,
                        );
                      }}
                    />
                    {t('common.settings.spendAutoTopUpEnabled')}
                  </label>
                  <label className="settings-page__field">
                    {t('common.settings.spendAutoTopUpTrigger')}
                    <input
                      className="fm-input"
                      type="number"
                      min={0}
                      step={1}
                      disabled={!spendLimits.autoTopUpEnabled}
                      value={spendLimits.autoTopUpTriggerCoins}
                      onChange={(event) => {
                        setSpendLimits((current) =>
                          current
                            ? {
                                ...current,
                                autoTopUpTriggerCoins: parseRequiredCoinsInput(event.target.value),
                              }
                            : current,
                        );
                      }}
                    />
                  </label>
                  <label className="settings-page__field">
                    {t('common.settings.spendAutoTopUpAmount')}
                    <input
                      className="fm-input"
                      type="number"
                      min={0}
                      step={1}
                      disabled={!spendLimits.autoTopUpEnabled}
                      value={spendLimits.autoTopUpAmountCoins}
                      onChange={(event) => {
                        setSpendLimits((current) =>
                          current
                            ? {
                                ...current,
                                autoTopUpAmountCoins: parseRequiredCoinsInput(event.target.value),
                              }
                            : current,
                        );
                      }}
                    />
                  </label>
                </fieldset>

                <div className="settings-page__footer">
                  <Button
                    label={
                      isSaving ? t('common.settings.saving') : t('common.settings.saveSpend')
                    }
                    disabled={isSaving}
                    onClick={() => {
                      void saveSpendLimits();
                    }}
                  />
                </div>
              </>
            ) : (
              <p className="settings-page__muted">{t('common.settings.spendUnavailable')}</p>
            )
          ) : null}

          {tab === 'notifications' ? (
            prefs ? (
              <>
                <p className="settings-page__muted">{t('common.settings.notificationsHint')}</p>

                <fieldset className="settings-page__fieldset">
                  <legend>{t('common.settings.channels')}</legend>
                  <label className="settings-page__check">
                    <input
                      type="checkbox"
                      checked={prefs.browserEnabled}
                      onChange={(event) => {
                        setPrefs((current) =>
                          current
                            ? { ...current, browserEnabled: event.target.checked }
                            : current,
                        );
                      }}
                    />
                    {t('common.settings.channelBrowser')}
                  </label>
                  <label className="settings-page__check">
                    <input
                      type="checkbox"
                      checked={prefs.emailEnabled}
                      onChange={(event) => {
                        setPrefs((current) =>
                          current ? { ...current, emailEnabled: event.target.checked } : current,
                        );
                      }}
                    />
                    {t('common.settings.channelEmail')}
                  </label>
                  <label className="settings-page__check">
                    <input
                      type="checkbox"
                      checked={prefs.smsEnabled}
                      onChange={(event) => {
                        setPrefs((current) =>
                          current ? { ...current, smsEnabled: event.target.checked } : current,
                        );
                      }}
                    />
                    {t('common.settings.channelSms')}
                  </label>
                  <label className="settings-page__field">
                    {t('common.settings.phone')}
                    <input
                      className="fm-input"
                      type="tel"
                      placeholder="+15551234567"
                      value={prefs.phoneE164 ?? ''}
                      disabled={!prefs.smsEnabled}
                      onChange={(event) => {
                        const value = event.target.value.trim();
                        setPrefs((current) =>
                          current
                            ? { ...current, phoneE164: value.length > 0 ? value : null }
                            : current,
                        );
                      }}
                    />
                  </label>
                </fieldset>

                <fieldset className="settings-page__fieldset">
                  <legend>{t('common.settings.events')}</legend>
                  <p className="settings-page__muted">{t('common.settings.eventsChannelHint')}</p>
                  <div className="settings-page__events-table">
                    <div className="settings-page__events-head">
                      <span>{t('common.settings.event')}</span>
                      <span>{t('common.settings.channelBrowser')}</span>
                      <span>{t('common.settings.channelEmail')}</span>
                      <span>{t('common.settings.channelSms')}</span>
                    </div>
                    {NOTIFICATION_EVENTS.map((event) => (
                      <div key={event} className="settings-page__events-row">
                        <span>{t(EVENT_LABEL_KEYS[event])}</span>
                        {(['browser', 'email', 'sms'] as const).map((channel) => (
                          <label key={channel} className="settings-page__events-cell">
                            <input
                              type="checkbox"
                              checked={prefs.events[event][channel]}
                              disabled={
                                (channel === 'browser' && !prefs.browserEnabled) ||
                                (channel === 'email' && !prefs.emailEnabled) ||
                                (channel === 'sms' && !prefs.smsEnabled)
                              }
                              onChange={(changeEvent) => {
                                const checked = changeEvent.target.checked;
                                setPrefs((current) => {
                                  if (!current) {
                                    return current;
                                  }
                                  return {
                                    ...current,
                                    events: {
                                      ...current.events,
                                      [event]: {
                                        ...current.events[event],
                                        [channel]: checked,
                                      },
                                    },
                                  };
                                });
                              }}
                            />
                          </label>
                        ))}
                      </div>
                    ))}
                  </div>
                </fieldset>

                <fieldset className="settings-page__fieldset">
                  <legend>{t('common.settings.perModel')}</legend>
                  <p className="settings-page__muted">{t('common.settings.perModelHint')}</p>
                  {modelPrefs.length === 0 ? (
                    <p className="settings-page__muted">{t('common.settings.perModelEmpty')}</p>
                  ) : (
                    <ul className="settings-page__model-notify-list">
                      {modelPrefs.map((model) => (
                        <li key={model.modelId} className="settings-page__model-notify">
                          <div className="settings-page__model-notify-head">
                            <Link
                              to={`/@${model.username}`}
                              className="settings-page__name"
                            >
                              {model.displayName}
                            </Link>
                            <span className="settings-page__handle">
                              {formatUsername(model.username)}
                              {model.isFavorite
                                ? ` · ${t('common.settings.faveBadge').replace('{{rank}}', String(model.favoriteRank))}`
                                : ''}
                            </span>
                          </div>
                          <div className="settings-page__model-notify-toggles">
                            {MODEL_EVENT_KEYS.map((key) => (
                              <label key={key} className="settings-page__check">
                                <input
                                  type="checkbox"
                                  checked={model.notifications[key]}
                                  disabled={savingModelId === model.modelId}
                                  onChange={(event) => {
                                    void updateModelNotification(
                                      model,
                                      key,
                                      event.target.checked,
                                    );
                                  }}
                                />
                                {t(EVENT_LABEL_KEYS[key])}
                              </label>
                            ))}
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                </fieldset>

                <div className="settings-page__footer">
                  <Button
                    label={
                      isSaving
                        ? t('common.settings.saving')
                        : t('common.settings.saveNotifications')
                    }
                    disabled={isSaving}
                    onClick={() => {
                      void saveNotifications();
                    }}
                  />
                </div>
              </>
            ) : (
              <p className="settings-page__muted">{t('common.settings.notificationsUnavailable')}</p>
            )
          ) : null}

          {tab === 'language' ? (
            <LanguageSwitcher
              variant="panel"
              onLocaleChange={(next) => {
                persistLocale(next);
                setSuccess(t('common.language.saved'));
              }}
            />
          ) : null}
        </div>
      )}
    </div>
  );
}
